import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { checkRetryPayment, fetchOtherStoreOrder, retryPayment } from "../api";
import { copyTextToClipboard } from "../helpers";
import { isTerminalOrder, isUnpaidFinal, isWaitingForFunds } from "../order-status";
import {
  dropOrderFromUrl,
  forgetRecentStoreOrder,
  getRecentStoreOrder,
  getRecentStoreOrderId,
  rememberOrderToken,
  saveRecentStoreOrder,
} from "../order-storage";
import OtherStoreSupportLinks from "./OtherStoreSupportLinks";
import RedeemCodesLink from "./RedeemCodesLink";
import PaymentRetryButton from "../../components/common/PaymentRetryButton";

function OtherStoreRecentCodes({
  storeId,
  orderId: orderIdProp,
  accessToken: accessTokenProp,
  /** После оплаты увести на /store/order/... (один poll на витрине, без второй кнопки). */
  redirectOnPaid = false,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderIdProp));
  const [copiedKey, setCopiedKey] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const storedOrder = getRecentStoreOrder(storeId);
  const orderId = orderIdProp || storedOrder?.orderId || getRecentStoreOrderId(storeId);
  const accessToken =
    accessTokenProp || storedOrder?.accessToken || null;

  useEffect(() => {
    if (orderIdProp && storeId) {
      saveRecentStoreOrder(storeId, orderIdProp, accessTokenProp);
    }
  }, [orderIdProp, storeId, accessTokenProp]);

  useEffect(() => {
    setDismissed(false);
  }, [orderId]);

  useEffect(() => {
    if (!orderId || dismissed) {
      setOrder(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timerId;

    const load = async () => {
      try {
        const data = await fetchOtherStoreOrder(orderId, { accessToken });
        if (!cancelled) {
          setOrder(data);
          if (data?.storeId && data.storeId !== storeId) {
            setOrder(null);
          }
        }
        return data;
      } catch {
        if (!cancelled) {
          setOrder(null);
        }
        return null;
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const schedule = (ms = 4000) => {
      timerId = window.setTimeout(poll, ms);
    };

    const poll = async () => {
      if (cancelled) {
        return;
      }
      if (typeof document !== "undefined" && document.hidden) {
        schedule(4000);
        return;
      }
      const data = await load();
      if (cancelled) {
        return;
      }
      if (redirectOnPaid && data?.paymentStatus === "SUCCESS") {
        window.location.href = `/store/order/${encodeURIComponent(orderId)}`;
        return;
      }
      if (!isTerminalOrder(data)) {
        schedule(4000);
      }
    };

    const onVisibility = () => {
      if (!cancelled && !document.hidden) {
        if (timerId) {
          window.clearTimeout(timerId);
        }
        schedule(0);
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [orderId, storeId, accessToken, dismissed, reloadKey, redirectOnPaid]);

  // Неоплаченный заказ показываем один раз: после обновления страницы блок снова пустой
  useEffect(() => {
    if (!isUnpaidFinal(order) || !orderId) {
      return;
    }
    forgetRecentStoreOrder(storeId, orderId);
    dropOrderFromUrl(orderId);
  }, [order, orderId, storeId]);

  // Неоплаченный заказ сервер ещё несколько часов сверяет с платёжкой — покупатель может скрыть его сам.
  const handleDismiss = () => {
    forgetRecentStoreOrder(storeId, orderId);
    dropOrderFromUrl(orderId);
    setDismissed(true);
  };

  if (!orderId || dismissed) {
    return (
      <section className="other-store-codes-panel" aria-live="polite">
        <h2 className="other-store-codes-title">Ваши коды</h2>
        <p className="other-store-codes-status">
          Здесь появятся ваши коды после оплаты: «Вернуться в магазин» в
          платёжке или «Назад» в браузере на эту страницу.
        </p>
      </section>
    );
  }

  const isPaid = order?.paymentStatus === "SUCCESS";
  const isFulfilled = order?.fulfillmentStatus === "SUCCESS";
  const isCancelled = order?.paymentStatus === "CANCELLED";
  const isFailed =
    order?.paymentStatus === "FAILED" || order?.fulfillmentStatus === "FAILED";
  const waitingFunds = isWaitingForFunds(order);
  const hasKeys = isFulfilled && order?.keys?.length > 0;
  const needsAuth = Boolean(order?.requiresAuth);

  const handleCopy = async (key) => {
    const copied = await copyTextToClipboard(key);
    if (copied) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  return (
    <section className="other-store-codes-panel" aria-live="polite">
      <h2 className="other-store-codes-title">Ваши коды</h2>

      {loading && !order ? (
        <p className="other-store-codes-status">Загрузка заказа…</p>
      ) : null}

      {!loading && !order ? (
        <p className="other-store-codes-status">Заказ не найден</p>
      ) : null}

      {order && needsAuth && isPaid ? (
        <p className="other-store-codes-status">
          Коды отправлены в письме на {order.email}. На сайте они видны только
          по персональной ссылке после оплаты.
        </p>
      ) : null}

      {order && (isFailed || isCancelled) ? (
        <p className="other-store-error">
          {order.paymentError ||
            order.fulfillmentError ||
            (isCancelled ? "Оплата отменена." : "Оплата не прошла.")}
        </p>
      ) : null}

      {order && !isPaid && !isFailed && !isCancelled ? (
        <p className="other-store-codes-status">Ожидаем подтверждение оплаты…</p>
      ) : null}

      {waitingFunds ? (
        <div className="other-store-delay-notice">
          <p className="other-store-codes-status">
            {order.fulfillmentError ||
              "Ваш код задерживается. Можете написать в техподдержку для уточнения сроков."}
          </p>
          <OtherStoreSupportLinks support={order.support} />
        </div>
      ) : null}

      {order && isPaid && !isFulfilled && !isFailed && !waitingFunds ? (
        <p className="other-store-codes-status">
          Оплата получена. Получаем код из магазина…
        </p>
      ) : null}

      {hasKeys ? (
        <>
          <ul className="other-store-keys-list other-store-keys-list--compact">
            {order.keys.map((item) => (
              <li key={item.id || item.key} className="other-store-key-item">
                <code>{item.key}</code>
                <button
                  type="button"
                  className="other-store-key-copy"
                  onClick={() => handleCopy(item.key)}
                >
                  {copiedKey === item.key ? "Скопировано" : "Скопировать"}
                </button>
                {item.productName ? (
                  <span className="other-store-key-meta">{item.productName}</span>
                ) : null}
              </li>
            ))}
          </ul>
          <RedeemCodesLink storeId={storeId} count={order.keys.length} />
          <p className="other-store-codes-note">
            Код также отправлен на {order.email}.
          </p>
        </>
      ) : null}

      {order ? (
        <div className="other-store-codes-actions">
          <Link
            to={`/store/order/${order.orderId}`}
            onClick={() => rememberOrderToken(order.orderId, accessToken)}
            className="other-store-codes-link"
          >
            Подробнее о заказе
          </Link>
          {!isPaid ? (
            <button
              type="button"
              className="other-store-codes-link"
              title="Скрыть этот заказ"
              onClick={handleDismiss}
            >
              Передумал
            </button>
          ) : null}
          {!isPaid ? (
            <PaymentRetryButton
              paymentRetry={order.paymentRetry}
              failed={isFailed || isCancelled}
              onCheck={() => checkRetryPayment(order.orderId, accessToken)}
              onCreate={() => retryPayment(order.orderId, accessToken)}
              onChanged={() => setReloadKey((value) => value + 1)}
              support={<OtherStoreSupportLinks support={order.support} />}
              className="mt-4 flex basis-full flex-col items-start gap-1.5"
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default OtherStoreRecentCodes;
